/**
 * Payment Detail Page
 */
import React, { useState, useMemo, type MouseEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Menu,
  ListSubheader,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import { useGetPaymentQuery, useUpdatePaymentStatusMutation, UpdatePaymentStatusData } from '../../store/api/paymentsApi';
import { usePermissions } from '../../hooks/usePermissions';
import {
  getAllowedNextPaymentStatuses,
  splitPaymentStatusesForMenu,
  PAYMENT_STATUS_LABELS,
  formatPaymentStatusUpdateError,
  type PaymentStatus,
} from '../../utils/paymentStatusTransitions';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const PaymentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  const { data: paymentData, isLoading, error } = useGetPaymentQuery(Number(id!), {
    skip: !id,
  });

  const [updateStatus, { isLoading: isUpdating }] = useUpdatePaymentStatusMutation();

  const payment = paymentData?.data;

  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [statusDialogBasisStatus, setStatusDialogBasisStatus] = useState('');
  const [newStatus, setNewStatus] = useState<UpdatePaymentStatusData['status'] | ''>('');
  const [adminNotes, setAdminNotes] = useState('');
  const [gatewayTransactionId, setGatewayTransactionId] = useState('');
  const [gatewayReference, setGatewayReference] = useState('');
  const [failureReason, setFailureReason] = useState('');

  const statusMenuSplit = useMemo(() => {
    if (!payment?.status) {
      return { progress: [] as PaymentStatus[], markFailed: [] as PaymentStatus[], refunds: [] as PaymentStatus[] };
    }
    return splitPaymentStatusesForMenu(getAllowedNextPaymentStatuses(payment.status));
  }, [payment?.status]);

  const statusDialogSplit = useMemo(() => {
    if (!statusDialogBasisStatus) {
      return { progress: [] as PaymentStatus[], markFailed: [] as PaymentStatus[], refunds: [] as PaymentStatus[] };
    }
    return splitPaymentStatusesForMenu(getAllowedNextPaymentStatuses(statusDialogBasisStatus));
  }, [statusDialogBasisStatus]);

  const closePaymentStatusMenu = () => setStatusMenuAnchor(null);

  const openPaymentStatusMenu = (event: MouseEvent<HTMLElement>) => {
    setStatusMenuAnchor(event.currentTarget);
  };

  const handlePaymentStatusMenuPick = (picked: PaymentStatus) => {
    if (!payment) return;
    setStatusDialogBasisStatus(payment.status);
    setNewStatus(picked);
    setAdminNotes('');
    setFailureReason('');
    closePaymentStatusMenu();
    setStatusDialog(true);
  };

  const resetStatusDialogFields = () => {
    setStatusDialog(false);
    setStatusDialogBasisStatus('');
    setNewStatus('');
    setAdminNotes('');
    setGatewayTransactionId('');
    setGatewayReference('');
    setFailureReason('');
  };

  // Check permissions
  const canUpdatePaymentStatus = hasPermission('update_payment_status');

  const handleStatusUpdate = async () => {
    if (!newStatus || !payment) return;

    try {
      const updateData: UpdatePaymentStatusData = {
        status: newStatus,
      };

      if (adminNotes) updateData.admin_notes = adminNotes;
      if (gatewayTransactionId) updateData.gateway_transaction_id = gatewayTransactionId;
      if (gatewayReference) updateData.gateway_reference = gatewayReference;
      if (newStatus === 'FAILED' && failureReason.trim()) {
        updateData.failure_reason = failureReason.trim();
      }

      await updateStatus({
        id: payment.id,
        data: updateData,
      }).unwrap();

      toast.success(t('servicePayments.toast.statusUpdatedSuccess'));
      resetStatusDialogFields();
    } catch (err: unknown) {
      toast.error(formatPaymentStatusUpdateError(err));
    }
  };

  const formatCurrency = (amount: string | number): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `EGP ${num.toFixed(2)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'PROCESSING':
        return 'info';
      case 'FAILED':
        return 'error';
      case 'REFUNDED':
      case 'PARTIALLY_REFUNDED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon fontSize="small" />;
      case 'PENDING':
        return <PendingIcon fontSize="small" />;
      case 'FAILED':
        return <CancelIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const handlePrintReceipt = () => {
    if (!payment) return;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error(t('servicePayments.toast.popupBlocked'));
      return;
    }

    const receiptContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t('servicePayments.receipt.title')} #${payment.id}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              color: #333;
            }
            .receipt-root {
              max-width: 720px;
              margin: 0 auto;
            }
            .receipt-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .receipt-header h1 {
              font-size: 28px;
              margin-bottom: 10px;
            }
            .receipt-header p {
              font-size: 14px;
              color: #666;
            }
            .receipt-section {
              margin-bottom: 25px;
            }
            .receipt-section h2 {
              font-size: 18px;
              margin-bottom: 15px;
              color: #333;
              border-bottom: 1px solid #ddd;
              padding-bottom: 8px;
            }
            .receipt-row {
              display: flex;
              justify-content: space-between;
              gap: 12px;
              padding: 8px 0;
              border-bottom: 1px dotted #ddd;
            }
            .receipt-row:last-child {
              border-bottom: none;
            }
            .receipt-label {
              font-weight: 600;
              color: #666;
              flex-shrink: 0;
            }
            .receipt-value {
              color: #333;
              text-align: right;
              word-break: break-word;
            }
            .receipt-amount {
              font-size: 20px;
              font-weight: 700;
              color: #1976d2;
            }
            .receipt-footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #333;
              text-align: center;
              font-size: 12px;
              color: #666;
              line-height: 1.4;
            }
            .receipt-footer p {
              margin: 0.15em 0;
            }
            .receipt-notes {
              padding: 10px;
              background: #f5f5f5;
              border-radius: 4px;
            }
            @media print {
              @page {
                margin: 8mm;
                size: A4 portrait;
              }
              html, body {
                height: auto !important;
                overflow: visible !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              body {
                padding: 0 !important;
                margin: 0 !important;
                font-size: 10.5px;
                line-height: 1.3;
              }
              .receipt-root {
                page-break-inside: avoid;
                break-inside: avoid;
                max-width: none;
              }
              .receipt-header {
                margin-bottom: 10px;
                padding-bottom: 8px;
              }
              .receipt-header h1 {
                font-size: 18px;
                margin-bottom: 4px;
              }
              .receipt-header p {
                font-size: 11px;
                line-height: 1.25;
              }
              .receipt-section {
                margin-bottom: 8px;
                page-break-inside: avoid;
                break-inside: avoid;
              }
              .receipt-section h2 {
                font-size: 12px;
                margin-bottom: 4px;
                padding-bottom: 3px;
              }
              .receipt-row {
                padding: 2px 0;
              }
              .receipt-amount {
                font-size: 14px;
              }
              .receipt-footer {
                margin-top: 10px;
                padding-top: 6px;
                font-size: 9px;
              }
              .receipt-footer p {
                margin: 0.1em 0;
              }
              .receipt-notes {
                padding: 4px 6px !important;
                background: #f9f9f9 !important;
              }
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
        <div class="receipt-root">
          <div class="receipt-header">
            <h1>${t('servicePayments.receipt.title')}</h1>
            <p>${t('servicePayments.receipt.org')}</p>
            <p>${t('servicePayments.receipt.receiptNo')} #${payment.id}</p>
            <p>${t('servicePayments.receipt.date')}: ${moment(payment.created_at).format('MMMM DD, YYYY HH:mm')}</p>
          </div>

          <div class="receipt-section">
            <h2>${t('servicePayments.receipt.paymentInformation')}</h2>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.status')}:</span>
              <span class="receipt-value">${t(`servicePayments.status.${payment.status}`, payment.status)}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.paymentMethod')}:</span>
              <span class="receipt-value">${t(`servicePayments.method.${payment.payment_method}`, payment.payment_method)}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.totalAmount')}:</span>
              <span class="receipt-value receipt-amount">${formatCurrency(payment.amount)}</span>
            </div>
            ${payment.paid_at ? `
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.paidAt')}:</span>
              <span class="receipt-value">${moment(payment.paid_at).format('MMMM DD, YYYY HH:mm')}</span>
            </div>
            ` : ''}
          </div>

          <div class="receipt-section">
            <h2>${t('servicePayments.receipt.orderInformation')}</h2>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.orderNumber')}:</span>
              <span class="receipt-value">${payment.order.order_number}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.service')}:</span>
              <span class="receipt-value">${payment.order.service_name}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.subservice')}:</span>
              <span class="receipt-value">${payment.order.subservice_name}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.patientName')}:</span>
              <span class="receipt-value">${payment.order.patient_name}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.scheduledDate')}:</span>
              <span class="receipt-value">${moment(payment.order.scheduled_date).format('MMMM DD, YYYY')}</span>
            </div>
          </div>

          ${payment.order_details ? `
          <div class="receipt-section">
            <h2>${t('servicePayments.receipt.orderDetails')}</h2>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.patientPhone')}:</span>
              <span class="receipt-value">${payment.order_details.patient_phone}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.patientAge')}:</span>
              <span class="receipt-value">${payment.order_details.patient_age} ${t('servicePayments.receipt.years')}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.location')}:</span>
              <span class="receipt-value">${payment.order_details.city}, ${payment.order_details.area}</span>
            </div>
            ${payment.order_details.scheduled_time_from ? `
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.scheduledTime')}:</span>
              <span class="receipt-value">${moment(payment.order_details.scheduled_time_from, 'HH:mm:ss').format('h:mm A')} - ${moment(payment.order_details.scheduled_time_to, 'HH:mm:ss').format('h:mm A')}</span>
            </div>
            ` : ''}
          </div>
          ` : ''}

          <div class="receipt-section">
            <h2>${t('servicePayments.receipt.customerInformation')}</h2>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.name')}:</span>
              <span class="receipt-value">${payment.user.full_name}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.email')}:</span>
              <span class="receipt-value">${payment.user.email}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.phone')}:</span>
              <span class="receipt-value">${payment.user.phone_number}</span>
            </div>
          </div>

          ${(payment.gateway_transaction_id || payment.fawry_reference_number || payment.gateway_reference) ? `
          <div class="receipt-section">
            <h2>${t('servicePayments.receipt.transactionDetails')}</h2>
            ${payment.gateway_transaction_id ? `
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.transactionId')}:</span>
              <span class="receipt-value">${payment.gateway_transaction_id}</span>
            </div>
            ` : ''}
            ${payment.gateway_reference ? `
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.gatewayReference')}:</span>
              <span class="receipt-value">${payment.gateway_reference}</span>
            </div>
            ` : ''}
            ${payment.fawry_reference_number ? `
            <div class="receipt-row">
              <span class="receipt-label">${t('servicePayments.receipt.fawryReference')}:</span>
              <span class="receipt-value">${payment.fawry_reference_number}</span>
            </div>
            ` : ''}
          </div>
          ` : ''}

          ${payment.notes ? `
          <div class="receipt-section">
            <h2>${t('servicePayments.receipt.notes')}</h2>
            <p class="receipt-notes">${payment.notes}</p>
          </div>
          ` : ''}

          <div class="receipt-footer">
            <p>${t('servicePayments.receipt.thanks')} &mdash; ${t('servicePayments.receipt.generated', { date: moment().format('MMM DD, YYYY HH:mm') })}</p>
          </div>
        </div>
        </body>
      </html>
    `;

    printWindow.document.write(receiptContent);
    printWindow.document.close();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      // Optionally close the window after printing
      // printWindow.close();
    }, 250);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !payment) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {t('servicePayments.notFound')}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/payments')}
          sx={{ mt: 2 }}
        >
          {t('servicePayments.buttons.backToPayments')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('servicePayments.detailTitle', { id: payment.id })}
        breadcrumbs={[
          { label: t('servicePayments.breadcrumbs.dashboard'), path: '/' },
          { label: t('servicePayments.breadcrumbs.payments'), path: '/payments' },
          { label: t('servicePayments.detailTitle', { id: payment.id }) },
        ]}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/payments')}
        sx={{ mb: 3 }}
      >
        {t('servicePayments.buttons.backToPayments')}
      </Button>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Main Content */}
        <Box>
          {/* Payment Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  {t('servicePayments.sections.paymentDetails')}
                </Typography>
                {canUpdatePaymentStatus && statusMenuSplit.progress.length + statusMenuSplit.markFailed.length + statusMenuSplit.refunds.length > 0 ? (
                  <Box
                    component="button"
                    type="button"
                    onClick={openPaymentStatusMenu}
                    aria-label={t('servicePayments.aria.changeStatus')}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.25,
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      margin: 0,
                      font: 'inherit',
                      color: 'inherit',
                      borderRadius: 1,
                      '&:hover': { opacity: 0.85 },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Chip
                      icon={getStatusIcon(payment.status)}
                      label={PAYMENT_STATUS_LABELS[payment.status as PaymentStatus] ?? payment.status}
                      size="small"
                      color={getStatusColor(payment.status) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                    />
                    <KeyboardArrowDownIcon sx={{ fontSize: '1.125rem', color: 'text.secondary', flexShrink: 0 }} />
                  </Box>
                ) : (
                  <Chip
                    icon={getStatusIcon(payment.status)}
                    label={PAYMENT_STATUS_LABELS[payment.status as PaymentStatus] ?? payment.status}
                    size="small"
                    color={getStatusColor(payment.status) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.totalAmount')}</Typography>
                  <Typography variant="body1" fontWeight={600} color="primary">
                    {formatCurrency(payment.amount)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.paymentMethod')}</Typography>
                  <Typography variant="body1">
                    <Chip label={t(`servicePayments.method.${payment.payment_method}`, payment.payment_method)} size="small" variant="outlined" />
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.serviceAmount')}</Typography>
                  <Typography variant="body1">{formatCurrency(payment.service_amount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.createdAt')}</Typography>
                  <Typography variant="body1">
                    {moment(payment.created_at).format('MMMM DD, YYYY HH:mm')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.updatedAt')}</Typography>
                  <Typography variant="body1">
                    {moment(payment.updated_at).format('MMMM DD, YYYY HH:mm')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.paidAt')}</Typography>
                  <Typography variant="body1">
                    {payment.paid_at
                      ? moment(payment.paid_at).format('MMMM DD, YYYY HH:mm')
                      : t('servicePayments.labels.notPaidYet')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Order Information */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('servicePayments.sections.orderInformation')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.orderNumber')}</Typography>
                  <Typography variant="body1">{payment.order.order_number}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.service')}</Typography>
                  <Typography variant="body1">{payment.order.service_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.subservice')}</Typography>
                  <Typography variant="body1">{payment.order.subservice_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.patientName')}</Typography>
                  <Typography variant="body1">{payment.order.patient_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.scheduledDate')}</Typography>
                  <Typography variant="body1">
                    {moment(payment.order.scheduled_date).format('MMMM DD, YYYY')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.orderStatus')}</Typography>
                  <Typography variant="body1">
                    <Chip label={payment.order.status} size="small" color="primary" variant="outlined" />
                  </Typography>
                </Box>
              </Box>

              {payment.order_details && (
                <>
                  <Divider sx={{ my: 3 }} />

                  {/* Order Details */}
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('servicePayments.sections.orderDetails')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.patientPhone')}</Typography>
                      <Typography variant="body1">{payment.order_details.patient_phone}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.patientGender')}</Typography>
                      <Typography variant="body1">
                        {payment.order_details.patient_gender === 'M' ? t('servicePayments.gender.male') : t('servicePayments.gender.female')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.patientAge')}</Typography>
                      <Typography variant="body1">{payment.order_details.patient_age} {t('servicePayments.receipt.years')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.scheduledTime')}</Typography>
                      <Typography variant="body1">
                        {moment(payment.order_details.scheduled_time_from, 'HH:mm:ss').format('h:mm A')} - {moment(payment.order_details.scheduled_time_to, 'HH:mm:ss').format('h:mm A')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.city')}</Typography>
                      <Typography variant="body1">{payment.order_details.city}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.area')}</Typography>
                      <Typography variant="body1">{payment.order_details.area}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.servicePrice')}</Typography>
                      <Typography variant="body1">{formatCurrency(payment.order_details.service_price)}</Typography>
                    </Box>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 3 }} />

              {/* User Information */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('servicePayments.sections.customerInformation')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.name')}</Typography>
                  <Typography variant="body1">{payment.user.full_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.email')}</Typography>
                  <Typography variant="body1">{payment.user.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.phone')}</Typography>
                  <Typography variant="body1">{payment.user.phone_number}</Typography>
                </Box>
              </Box>

              {payment.provider && (
                <>
                  <Divider sx={{ my: 3 }} />

                  {/* Provider Information */}
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('servicePayments.sections.providerInformation')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.name')}</Typography>
                      <Typography variant="body1">{payment.provider.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.type')}</Typography>
                      <Typography variant="body1">
                        <Chip label={payment.provider.type} size="small" variant="outlined" />
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.phone')}</Typography>
                      <Typography variant="body1">{payment.provider.phone}</Typography>
                    </Box>
                    {payment.provider.email && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('servicePayments.labels.email')}</Typography>
                        <Typography variant="body1">{payment.provider.email}</Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}


              {(payment.notes || payment.failure_reason) && (
                <>
                  <Divider sx={{ my: 3 }} />

                  {/* Additional Information */}
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('servicePayments.sections.additionalInformation')}
                  </Typography>
                  {payment.notes && (
                    <Box sx={{ mb: payment.failure_reason ? 2 : 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('servicePayments.labels.notes')}
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                        <Typography variant="body2">{payment.notes}</Typography>
                      </Paper>
                    </Box>
                  )}
                  {payment.failure_reason && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('servicePayments.labels.failureReason')}
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                        <Typography variant="body2">{payment.failure_reason}</Typography>
                      </Paper>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box>
          {/* Actions Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('servicePayments.sections.actions')}
              </Typography>

              {canUpdatePaymentStatus &&
                statusMenuSplit.progress.length + statusMenuSplit.markFailed.length + statusMenuSplit.refunds.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('servicePayments.noStatusChanges')}
                  </Typography>
                )}

              {/* Print Receipt - Available to all who can view the payment */}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrintReceipt}
              >
                {t('servicePayments.buttons.printReceipt')}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor && payment)}
        onClose={closePaymentStatusMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 8,
          sx: { minWidth: 220, maxHeight: 360, mt: 0.5 },
        }}
      >
        {statusMenuSplit.progress.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.nextStatus')}</ListSubheader>
        )}
        {statusMenuSplit.progress.map((key) => (
          <MenuItem key={key} onClick={() => handlePaymentStatusMenuPick(key)} dense>
            {PAYMENT_STATUS_LABELS[key]}
          </MenuItem>
        ))}
        {statusMenuSplit.markFailed.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.markAsFailed')}</ListSubheader>
        )}
        {statusMenuSplit.markFailed.map((key) => (
          <MenuItem
            key={key}
            onClick={() => handlePaymentStatusMenuPick(key)}
            dense
            sx={{ color: 'error.main' }}
          >
            {PAYMENT_STATUS_LABELS[key]}
          </MenuItem>
        ))}
        {statusMenuSplit.refunds.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.refunds')}</ListSubheader>
        )}
        {statusMenuSplit.refunds.map((key) => (
          <MenuItem key={key} onClick={() => handlePaymentStatusMenuPick(key)} dense>
            {PAYMENT_STATUS_LABELS[key]}
          </MenuItem>
        ))}
      </Menu>

      {/* Update Status Dialog */}
      <Dialog open={statusDialog} onClose={resetStatusDialogFields} maxWidth="sm" fullWidth>
        <DialogTitle>{t('servicePayments.dialog.title')}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {payment && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('servicePayments.dialog.currentStatus')}{' '}
              <strong>{PAYMENT_STATUS_LABELS[payment.status as PaymentStatus] ?? payment.status}</strong>{'. '}
              {t('servicePayments.dialog.transitionsHint')}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('servicePayments.dialog.newStatus')}</InputLabel>
            <Select
              value={newStatus}
              label={t('servicePayments.dialog.newStatus')}
              onChange={(e) => setNewStatus(e.target.value as UpdatePaymentStatusData['status'])}
              MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
            >
              {statusDialogSplit.progress.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.nextStatus')}</ListSubheader>
              )}
              {statusDialogSplit.progress.map((key) => (
                <MenuItem key={key} value={key}>
                  {PAYMENT_STATUS_LABELS[key]}
                </MenuItem>
              ))}
              {statusDialogSplit.markFailed.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.markAsFailed')}</ListSubheader>
              )}
              {statusDialogSplit.markFailed.map((key) => (
                <MenuItem key={key} value={key}>
                  {PAYMENT_STATUS_LABELS[key]}
                </MenuItem>
              ))}
              {statusDialogSplit.refunds.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('servicePayments.menu.refunds')}</ListSubheader>
              )}
              {statusDialogSplit.refunds.map((key) => (
                <MenuItem key={key} value={key}>
                  {PAYMENT_STATUS_LABELS[key]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {newStatus === 'FAILED' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('servicePayments.dialog.failureReason')}
              value={failureReason}
              onChange={(e) => setFailureReason(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('servicePayments.dialog.adminNotes')}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder={t('servicePayments.dialog.adminNotesPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetStatusDialogFields}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleStatusUpdate}
            disabled={
              !newStatus ||
              isUpdating ||
              (newStatus === 'FAILED' && !failureReason.trim())
            }
          >
            {isUpdating ? t('servicePayments.buttons.updating') : t('servicePayments.buttons.updateStatus')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentDetailPage;

